import { styled } from "@mui/material/styles";
import { Box, Chip, Tab, Tabs, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { withLayout } from "../hoc/with-layout";
import { Category as CategoryType } from "../data/mock/types";
import { DataContext } from "../data-provider";
import { useContext, useEffect, useMemo, useState } from "react";
import { RoleContext } from "../role-provider";
import { useAxios } from "../axios-provider";
import { useCustomQuery } from "../hooks/use-custom-query";
import { getElementLiveStatus } from "../data/items";

const pageURIs = [
  {
    title: "Marketing Information",
    path: "category-marketing-information",
    showInTabMenu: true,
  },
  {
    title: "Category Item Mapping",
    path: "category-item-mapping",
    showInTabMenu: true,
  },
  {
    title: "Review and Submit",
    path: "category-review-and-submit",
    showInTabMenu: true,
  },
  {
    title: "Compare With Live",
    path: "compare-with-live",
    showInTabMenu: false,
  },
];
const tabs = pageURIs.filter((uri) => uri.showInTabMenu === true);

type CategoryContextType = {
  category: CategoryType;
  categoryId: number;
  editable: boolean;
  isLive: boolean;
  language: string;
  setLanguage: (language: string) => void;
};

export function useCategory() {
  return useOutletContext<CategoryContextType>();
}

const CategoryComponent = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useAxios();

  const { pathname } = useLocation();
  const pathParts = pathname.split("/");
  const activeURI = pathParts[pathParts.length - 1];
  const activePage = pageURIs.find((uri) => uri.path === activeURI);

  const activeTab = tabs.find((tab) => tab.path === activePage?.path);

  if (!categoryId) {
    throw new Error("categoryId is required");
  }
  if (!activePage) {
    throw new Error("Invalid URI");
  }
  const { categoryList } = useContext(DataContext);
  const { defaultLanguage, selectedCountry } = useContext(RoleContext);
  const [language, setLanguage] = useState<string>(
    typeof defaultLanguage === "string" ? defaultLanguage : "EN"
  );
  const [isLive, setIsLive] = useState(false);
  const categoryName = useMemo(() => {
    if (categoryList.length > 0) {
      const category = categoryList.find(
        (p) => p.categoryId === Number(categoryId)
      );
      if (!category) {
        const errorMessage = `No category found matching id ${categoryId}`;
        navigate(`/error?message=${errorMessage}`);
        return;
      }
      return category.categoryName;
    }
    return "";
  }, [categoryList, categoryId, navigate]);

  const title = categoryName + " - " + activePage.title;

  const editable = useMemo(() => {
    const category = categoryList.find(
      (p) => p.categoryId === Number(categoryId)
    );
    if (category?.showEdit === "Y") {
      return true;
    } else {
      return false;
    }
  }, [categoryList, categoryId]);

  const getElementLiveStatusQuery = useCustomQuery(
    [
      "getElementLiveStatus",
      {
        categoryId,
        countryCode: selectedCountry,
      },
    ],
    () =>
      getElementLiveStatus(apiClient)({
        elementId: categoryId,
        countryCode: selectedCountry!,
      })
  );
  useEffect(() => {
    if (
      getElementLiveStatusQuery.isSuccess &&
      typeof getElementLiveStatusQuery.data.data.live === "string"
    ) {
      setIsLive(getElementLiveStatusQuery.data.data.live === "Y");
    }
  }, [getElementLiveStatusQuery.isSuccess, getElementLiveStatusQuery.data]);

  const CategoryWithLayout = useMemo(() => {
    return withLayout(() => {
      return (
        <StyledFlexBox>
          <Grid container>
            {activeTab && (
              <>
                <StyledTitleHeader container mobile={9}>
                  <Grid>
                    <Typography variant="h1">{categoryName}</Typography>
                  </Grid>
                  {isLive ? (
                    <Chip label="Live" color="success" />
                  ) : (
                    <Chip label="Not Live" />
                  )}
                </StyledTitleHeader>
                <StyledCenteredContainer mobile={12}>
                  <StyledTabs
                    value={activeTab.path}
                    scrollButtons="auto"
                    centered
                    aria-label="tab buttons"
                    TabIndicatorProps={{
                      sx: { backgroundColor: "#FFBC0D" },
                    }}
                  >
                    {tabs.map((tab) => (
                      <StyledTab
                        key={tab.path}
                        label={tab.title}
                        component={Link}
                        aria-label={tab.title}
                        to={`/categories/${categoryId}/${tab.path}`}
                        value={tab.path}
                        role="tab"
                      />
                    ))}
                  </StyledTabs>
                </StyledCenteredContainer>
              </>
            )}
            <Grid mobile={12}>
              <Outlet
                context={{
                  categoryId,
                  categoryName,
                  editable,
                  isLive,
                  language,
                  setLanguage,
                }}
              />
            </Grid>
          </Grid>
        </StyledFlexBox>
      );
    }, title);
  }, [activeTab, categoryId, categoryName, editable, isLive, language, title]);

  return <CategoryWithLayout />;
};

export const Category = CategoryComponent;

const StyledFlexBox = styled(Box)({
  flexGrow: 1,
  paddingLeft: 0,
  paddingRight: 0,
});
const StyledTitleHeader = styled(Grid)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(4),
}));
const StyledCenteredContainer = styled(Grid)(({ theme }) => ({
  justifyContent: "center",
  display: "flex",
  width: "100%",
  borderBottom: "1px solid #F6F6F6",
  marginBottom: theme.spacing(4),
}));
const StyledTabs = styled(Tabs)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #F6F6F6",
  padding: theme.spacing(0, 2),
}));
const StyledTab = styled(Tab)(({ theme }) => ({
  color: "black",
  fontSize: 18,
  fontFamily: "Speedee-Bold, Arial, Helvetica, sans-serif",
  letterSpacing: "-0.15px",
  textTransform: "initial",
  lineHeight: "28px",
  borderBottom: "2px solid #F6F6F6",
  "&.Mui-selected": {
    backgroundColor: "#F6F6F6",
    borderWidth: "4px",
    color: "black",
    boxShadow:
      "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
  },
})) as typeof Tab;
